import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { CS_FONT, CS_WEIGHT, csTokens } from 'components/CallScribe/callScribeTheme';

const ITEM_WIDTH = 68;
const FACE = 48;

const usePeopleStripStyles = makeStyles((theme: Theme) => {
  const t = csTokens(theme);
  const stageButton = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    minHeight: 44,
    padding: '0 12px',
    borderRadius: 10,
    border: 'none',
    background: t.glass,
    boxShadow: `inset 0 0 0 1px ${t.glassBorder}`,
    color: t.onGround,
    ...CS_FONT.small,
    fontWeight: CS_WEIGHT.strong,
    cursor: 'pointer',
    transition: 'background-color 150ms ease-out',
    '& svg': { fontSize: 18 },
    '&:hover:not(:disabled)': { background: t.glassStrong },
    '&:focus-visible': { outline: `2px solid ${t.stageFocus}`, outlineOffset: 2 },
    '&:disabled': { cursor: 'default', opacity: 0.6 },
  };
  return {
    srOnly: {
      position: 'absolute',
      width: 1,
      height: 1,
      margin: -1,
      padding: 0,
      overflow: 'hidden',
      clip: 'rect(0 0 0 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
    root: {
      flex: '0 0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
      color: t.onGround,
    },
    row: {
      listStyle: 'none',
      margin: 0,
      padding: '2px 0 4px',
      display: 'flex',
      flexWrap: 'nowrap',
      gap: 4,
      overflowX: 'auto',
      overflowY: 'hidden',
      overscrollBehaviorX: 'contain',
      scrollbarWidth: 'thin',
      scrollbarColor: `${t.glassBorder} transparent`,
      '& > li:first-child': { marginInlineStart: 'auto' },
      '& > li:last-child': { marginInlineEnd: 'auto' },
    },
    item: {
      flex: '0 0 auto',
      width: ITEM_WIDTH,
    },
    person: {
      width: '100%',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      padding: '4px 2px 6px',
      borderRadius: 12,
      color: t.onGround,
      textAlign: 'center',
      '@media (max-height: 799px)': {
        position: 'relative',
        padding: 2,
      },
    },
    personButton: {
      border: 'none',
      background: 'transparent',
      font: 'inherit',
      cursor: 'pointer',
      transition: 'background-color 150ms ease-out',
      '&:hover:not(:disabled)': { background: t.glass },
      '&:focus-visible': { outline: `2px solid ${t.stageFocus}`, outlineOffset: -2 },
      '&:disabled': { cursor: 'default', opacity: 0.45 },
    },
    face: {
      position: 'relative',
      isolation: 'isolate',
      display: 'grid',
      placeItems: 'center',
      width: FACE,
      height: FACE,
      marginBottom: 4,
      borderRadius: '50%',
      '@media (max-height: 799px)': {
        marginBottom: '0px',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: -3,
        borderRadius: '50%',
        pointerEvents: 'none',
      },
    },
    faceOpen: {
      '&::after': { boxShadow: `0 0 0 2px ${t.stageFocus}` },
    },
    faceRinging: {
      '&::after': {
        border: `2px solid ${t.stageWarn}`,
        borderTopColor: 'transparent',
        animation: '$ringing 1400ms linear infinite',
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          borderStyle: 'dashed',
          borderTopColor: t.stageWarn,
        },
      },
    },
    '@keyframes ringing': {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    mutedBadge: {
      position: 'absolute',
      right: -3,
      bottom: -3,
      zIndex: 1,
      width: 20,
      height: 20,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      fontSize: 13,
      background: t.stageWarn,
      color: t.stageWarnInk,
      boxShadow: `0 0 0 2px ${t.ground}`,
    },
    shortName: {
      ...CS_FONT.caption,
      fontWeight: CS_WEIGHT.strong,
      color: t.onGround,
      maxWidth: '100%',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
      '@media (max-height: 799px)': {
        position: 'absolute',
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
        display: 'block',
      },
    },
    state: {
      ...CS_FONT.caption,
      fontWeight: 600,
      color: t.onGroundMuted,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '@media (max-height: 799px)': {
        position: 'absolute',
        width: 1,
        height: 1,
        margin: -1,
        padding: 0,
        overflow: 'hidden',
        clip: 'rect(0 0 0 0)',
        whiteSpace: 'nowrap',
        border: 0,
        display: 'block',
      },
    },
    add: {},
    addFace: {
      display: 'grid',
      placeItems: 'center',
      width: FACE,
      height: FACE,
      marginBottom: 4,
      '@media (max-height: 799px)': {
        marginBottom: '0px',
      },
      boxSizing: 'border-box',
      borderRadius: '50%',
      border: `1.5px dashed ${t.onGroundMuted}`,
      color: t.onGround,
      fontSize: 22,
    },
    tray: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      padding: '6px 6px 6px 14px',
      borderRadius: 14,
      background: t.glassStrong,
      boxShadow: `inset 0 0 0 1px ${t.glassBorder}`,
      animation: '$trayIn 200ms ease-out',
      '&:focus-visible': { outline: `2px solid ${t.stageFocus}`, outlineOffset: 2 },
      '@media (prefers-reduced-motion: reduce)': { animation: '$fadeIn 150ms linear' },
    },
    '@keyframes trayIn': {
      from: { opacity: 0, transform: 'translateY(-4px)' },
      to: { opacity: 1, transform: 'none' },
    },
    '@keyframes fadeIn': {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    trayWho: {
      flex: '1 1 140px',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    trayName: {
      ...CS_FONT.small,
      fontWeight: CS_WEIGHT.strong,
      color: t.onGround,
      overflowWrap: 'anywhere',
    },
    trayMeta: {
      ...CS_FONT.caption,
      fontWeight: 600,
      color: t.onGroundMuted,
    },
    trayActions: {
      display: 'flex',
      gap: theme.spacing(1),
      flex: '0 0 auto',
    },
    trayButton: stageButton,
    trayRemove: {
      color: t.stageEndText,
    },
    button: stageButton,
    banner: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: '6px 6px 6px 12px',
      borderRadius: 12,
      background: alpha(t.stageWarn, 0.14),
      boxShadow: `inset 0 0 0 1px ${alpha(t.stageWarn, 0.45)}`,
      ...CS_FONT.small,
      color: t.onGround,
    },
    bannerText: { flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' },
    error: {
      ...CS_FONT.small,
      color: t.onGround,
      background: alpha(t.stageEnd, 0.16),
      boxShadow: `inset 0 0 0 1px ${alpha(t.stageEnd, 0.5)}`,
      borderRadius: 12,
      padding: theme.spacing(1, 1.5),
      margin: 0,
    },
  };
});

export default usePeopleStripStyles;
